import { type CarrierDef } from '../common.js'
import { type DocumentType } from '../document-types.js'

const credentialTypes = [
  'client-id', // engine clients
  'client-secret', // engine clients
  'spot-volume-api-key',
  'child-key',
  'child-secret',
  'express-account-number',
  'express-account-address',
  'express-account-city',
  'express-account-state',
  'express-account-postalcode',
  'express-account-country',
  'max-linear-feet',
] as const

const locationCredentialTypes = [
  'freight-account-number',
  'freight-account-address',
  'freight-account-city',
  'freight-account-state',
  'freight-account-postalcode',
  'freight-account-country',
  'direct-account-number',
  'direct-account-address',
  'direct-account-city',
  'direct-account-state',
  'direct-account-postalcode',
  'direct-account-country',
] as const

export type fxfeCredType =
  | (typeof credentialTypes)[number]
  | (typeof locationCredentialTypes)[number]

export const fxfeDocumentTypes = [
  'invoice',
  'proof-of-delivery',
  'signed-bol',
] as const satisfies readonly DocumentType[]

export const fxfe: CarrierDef<fxfeCredType> = {
  name: 'FedEx',
  scac: 'fxfe',
  ltlServices: [
    { id: 'freight_economy', name: 'FedEx Freight® Economy' },
    {
      id: 'freight_economy_am_delivery',
      name: 'FedEx Freight® Economy (A.M. Delivery)',
      guaranteed: true,
      guaranteedByTime: '10:00',
    },
    {
      id: 'freight_economy_close_of_business',
      name: 'FedEx Freight® Economy (Close of Business Delivery)',
      guaranteed: true,
      guaranteedByTime: '17:00',
    },
    { id: 'freight_priority', name: 'FedEx Freight® Priority' },
    {
      id: 'freight_priority_am_delivery',
      name: 'FedEx Freight® Priority (A.M. Delivery)',
      guaranteed: true,
      guaranteedByTime: '10:00',
    },
    {
      id: 'freight_priority_close_of_business',
      name: 'FedEx Freight® Priority (Close of Business Delivery)',
      guaranteed: true,
      guaranteedByTime: '17:00',
    },
    {
      id: 'freight_direct_basic_economy',
      name: 'FedEx Freight® Direct Basic Economy',
    },
    {
      id: 'freight_direct_basic_by_appointment_economy',
      name: 'FedEx Freight® Direct Basic by Appointment Economy',
    },
    {
      id: 'freight_direct_basic_priority',
      name: 'FedEx Freight® Direct Basic Priority',
    },
    {
      id: 'freight_direct_basic_by_appointment_priority',
      name: 'FedEx Freight® Direct Basic by Appointment Priority',
    },
    {
      id: 'freight_direct_premium',
      name: 'FedEx Freight® Direct Premium',
    },
    {
      id: 'freight_direct_standard',
      name: 'FedEx Freight® Direct Standard',
    },
    {
      id: 'volume_truckload_exempt',
      name: 'Volume Truckload Exempt (TLX)',
    },
    {
      id: 'volume_truckload_service',
      name: 'Volume Truckload Service (TLS)',
    },
    {
      id: 'volume_truckload_exclusive_use',
      name: 'Volume Truckload Exclusive Use (EXL)',
    },
  ],
  parcelServices: [
    { id: 'ground', name: 'FedEx Ground®' },
    { id: 'home_delivery', name: 'FedEx Home Delivery®' },
    { id: 'first_overnight', name: 'FedEx First Overnight®' },
    { id: 'priority_overnight', name: 'FedEx Priority Overnight®' },
    { id: 'standard_overnight', name: 'FedEx Standard Overnight®' },
    { id: 'two_day_am', name: 'FedEx 2Day® A.M.' },
    { id: 'two_day', name: 'FedEx 2Day®' },
    { id: 'express_saver', name: 'FedEx Express Saver®' },
  ],
  credentialTypes,
  locationCredentialTypes,
  legacyCredMap: [
    ['client-id', 'clientId'],
    ['client-secret', 'clientSecret'],
    ['spot-volume-api-key', 'spotVolumeAPIKey'],
    ['express-account-number', 'ref2'],
    ['express-account-number', 'accountNumber'],
    ['freight-account-number', 'ref5'],
  ],
  engineLayers: [
    'provisioning',
    'rating',
    'bol',
    'pickup',
    'cancel',
    'profinder',
    'tracking',
    'documents',
  ],
  documentTypes: fxfeDocumentTypes,
  credentialConfig: {
    'freight-account-number': { customerIdentifier: true },
    'direct-account-number': { customerIdentifier: true },
    'express-account-number': { customerIdentifier: true },
    'freight-account-address': { nonsensitive: true },
    'freight-account-city': { nonsensitive: true },
    'freight-account-state': { nonsensitive: true },
    'freight-account-postalcode': { nonsensitive: true },
    'freight-account-country': { nonsensitive: true },
    'direct-account-address': { nonsensitive: true },
    'direct-account-city': { nonsensitive: true },
    'direct-account-state': { nonsensitive: true },
    'direct-account-postalcode': { nonsensitive: true },
    'direct-account-country': { nonsensitive: true },
    'express-account-address': { nonsensitive: true },
    'express-account-city': { nonsensitive: true },
    'express-account-state': { nonsensitive: true },
    'express-account-postalcode': { nonsensitive: true },
    'express-account-country': { nonsensitive: true },
    'client-id': { hideFromFreightview: true },
    'client-secret': { hideFromFreightview: true },
  },
}
